<script setup>
import { computed } from 'vue';
import { OVERVIEW, AREA_NAMES } from './data/reportData';
import { detailAreasByCode } from './data/reportLogic';
import icon01 from '@/assets/images/result/ai/icon_01.svg';
import icon02 from '@/assets/images/result/ai/icon_02.svg';
import icon03 from '@/assets/images/result/ai/icon_03.svg';
import icon04 from '@/assets/images/result/ai/icon_04.svg';

const icons = [icon01, icon02, icon03, icon04];
const codes = ['a', 'b', 'c', 'd'];

const props = defineProps({
  reportType: {
    type: String,
    required: true,
    validator: (value) => ['student', 'teacher', 'principal', 'parent'].includes(value),
  },
  reportData: {
    type: Object,
    default: () => ({
        schoolName: '',
        studentName: '',
        studentNum: '',
        studentGender: '',
        grade: '',
        className: '',
        totalStudent: '',
        teacherName: '',
        date: '',
        schul_type: '', // 학교급 'E' | 'M' | 'H'
    }),
  },
});

// 영역 안내 4행 (엑셀 00_진단검사 개요)
const areaRows = computed(() =>
  AREA_NAMES.map((name, i) => ({ name, icon: icons[i], desc: OVERVIEW.areaDesc[name] || '' }))
);

// 하위 영역 (학교급별 세부영역 시트 — 현재는 초/중/고 동일)
const subAreaRows = computed(() => {
  const grouped = detailAreasByCode(props.reportData?.schul_type);
  return AREA_NAMES.map((name, i) => ({ name, icon: icons[i], items: grouped[codes[i]] || [] }));
});
</script>
<template>   
  <h3 class="report-title pt-4">
    AI·디지털 리터러시 진단 결과
    <span v-if="props.reportType === 'student'">(학생용)</span>
    <span v-if="props.reportType === 'teacher'">(학급용)</span>
    <span v-if="props.reportType === 'principal'">(학교용)</span>
  </h3>
  <div class="info"> 
    <template v-if="props.reportType ===  'student' || props.reportType === 'parent'">      
      <p><span>학교</span>{{ props.reportData.schoolName }}</p> 
      <p><span>학생</span>{{ props.reportData.grade }}학년 {{ props.reportData.className }}반 {{ props.reportData.studentNum }}번 {{ props.reportData.studentName }}</p>        
    </template>
    <template v-else-if="props.reportType === 'teacher'">         
      <p><span>학교</span>{{ props.reportData.schoolName }}</p>   
      <p><span>학급</span>{{ props.reportData.grade }}학년 {{ props.reportData.className }}반</p> 
      <p><span>담임</span>{{ props.reportData.teacherName }}</p> 
    </template>      
    <template v-else-if="props.reportType === 'principal'">         
      <p style="min-width: auto; text-align: center; display: block;">{{ props.reportData.schoolName }}</p>    
    </template>    
  </div>  
  <div class="ai-intro pb-3">
    <h4 class="title-box">AI·디지털 리터러시 진단검사 개요</h4>
    <div class="ai-table pt-1">
      <div class="row">
          <div class="th"><span>정의</span></div>
          <div class="td">{{ OVERVIEW.definition }}</div>
      </div>
      <div class="row">
          <div class="th"><span>검사 목적</span></div>
          <div class="td">{{ OVERVIEW.purpose }}</div>
      </div>
    </div>
  </div>
  <div class="card-wrap">
    <div class="card area-guide">
      <h5 class="card-title">AI·디지털 리터러시 영역 안내</h5>
      <div class="ai-table pt-2 pb-5">
        <div class="row" v-for="row in areaRows" :key="row.name">
          <div class="th">
            <img :src="row.icon">
            <p>{{ row.name }}</p>
          </div>
          <div class="td">{{ row.desc }}</div>
        </div> 
      </div>
    </div>
    <div class="card sub-area">
      <h5 class="card-title">AI·디지털 리터러시 하위 영역</h5>
      <div class="ai-table pt-2">
        <template v-for="(pair, pi) in [[subAreaRows[0], subAreaRows[1]], [subAreaRows[2], subAreaRows[3]]]" :key="pi">
          <div class="row">
            <div v-for="(row, ri) in pair" :key="row.name" :class="ri === 0 ? 'th' : 'td'">
              <div class="icon-wrap">
                <img :src="row.icon">
                <span>{{ row.name }}</span>
              </div>           
              <p>
                <span v-for="item in row.items" :key="item">{{ item }}</span>
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="character">
      <img src="@/assets/images/result/ai/char_01.png">
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .report-title {
    font-family: var(--smoe-font-visual);
    font-size: 2.5rem;
    font-weight: 700;
    text-align: center;
    padding-bottom: 0.625rem;
  }
  .info {
    border: 1px solid var( --result-ai-border);
    border-radius: .375rem;
    // 학교명이 길면 옆 칸(학급)을 덮어써서, 칸 폭을 넓히고 박스는 내용에 맞춰 늘린다
    width: fit-content;
    max-width: 100%;
    padding: .875rem 2.188rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    /* justify-items: start; */
    justify-content: center;
    margin: 0 auto .75rem;
    row-gap: .938rem;
    /* text-align: center; */
    p { 
      display: flex;
      align-items: center;    
      justify-content: flex-start;      
      justify-self: center; 
      /* width: fit-content; 
      min-width: 220px;   */
      width: 330px;
      span {
        font-weight: 700; 
        color: var(--result-ai-primary-800); 
        padding-right: 1.5rem;        
        flex-shrink: 0;
        text-align: left;
      }     
      // 학생·학부모용(학교/학생 2줄)은 한 줄을 통째로 쓰고,
      // 폭이 모자라 이름이 다음 줄로 내려가지 않도록 줄바꿈을 막는다.
      &:first-child:nth-last-child(2),
      &:first-child:nth-last-child(2) ~ p {
        grid-column: 1 / -1;
        white-space: nowrap;
      }
      &:only-child {
        grid-column: 1 / -1;
      }
    }
  }

  .ai-intro {
    .ai-table { 
      .th {  
        padding: .5rem;
        span {
          font-size: 1.5rem; 
          font-weight: 700; 
          color: var(--result-ai-primary-800); 
          background-color: var( --result-ai-primary-50);
          border-radius: .25rem;
          width: 115px;
          height: 80px;
          display: flex;
          justify-content: center;
          align-items: center;       
        }
      }      
      .td {
        font-size: 1.25rem;
        padding: 1rem .75rem;
        text-align: justify;
        word-break: keep-all;
        text-justify: inter-word;
      }
    }
  }

  .area-guide {
    .ai-table .row > div {       
      &.th {
        gap: 1rem; padding: .5rem 1.5rem; justify-content: flex-start;
        img { width: 100px; }
        p { text-align: center; flex: 1; font-weight: 700; color: var(--result-ai-primary-800); }
      }
      &.td { 
        padding: 1.5rem 2rem; 
        line-height: 1.6; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        text-align: justify;
        word-break: keep-all;
        text-justify: inter-word;
      }
    }
  }

  .sub-area {
    .ai-table {
      display: flex;   
      flex-direction: column;
      width: 100%;
      .row { 
        display: flex; 
        .th, .td { 
          width: 50%; 
          display: flex; 
          justify-content: center;
          align-items: center;
          padding: .5rem 2rem 1rem;
          > div {
            width: 40%; 
            img { width: 100px; }
          }
          > p { width: 60%; }
        }
        .th { justify-content: space-between; }
      }  
      .icon-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
        span {
          font-weight: 700;
          color: var(--result-ai-primary-800);
          padding-top: .5rem;     
        }
      }
      p {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 235px;   
        padding-right: 2.375rem;       
        span { 
          border-bottom: 1px solid var(--result-ai-border); 
          width: 100%;
          text-align: center;
          padding: .25rem 0 ; 
          &:last-child { border: 0; }
        }
      }
    }
  }
  .character { position: absolute; right: 0; bottom: 0; }
</style>